'use client';

import type {DisplayShippingProps as Props} from './typings';

import ActionText from '@/atoms/ActionText/ActionText';
import {StyledDisplayShippingContainer} from './styles';
import {getShippingDisplayContent} from './utils';

export const TAXES_INFO_URL =
  'https://help.artfinder.com/article/826-will-i-be-charged-any-duties-or-taxes-for-my-order';

/**
 * @description Show shipping information and trigger estimate modal.
 * @param {Props} props - component props.
 * @param {Object|null} props.estimate - The shipping estimate object if available.
 * @param {string} props.productSlug - The slug of the product.
 * @param {Function} props.handleStartEstimate - Function to trigger the estimate process, open modal.
 * @param {Object|null} props.initialShippingInfo - Initial shipping information if available.
 * @param {string} props.productType - The type of the product (e.g., 'original' or 'prints').
 * @returns {JSX.Element} The rendered DisplayShipping component markup.
 */
const DisplayShipping = ({
  estimate,
  productSlug,
  handleStartEstimate,
  initialShippingInfo,
  productType,
}: Props): JSX.Element => {
  const content = getShippingDisplayContent({
    estimate,
    productSlug,
    productType,
    initialShippingInfo,
  });

  return (
    <StyledDisplayShippingContainer>
      <div>
        <p>Delivery</p>
        <div>
          <ActionText fontSize={14} onClick={handleStartEstimate}>
            Estimate Shipping Costs
          </ActionText>
        </div>
      </div>

      <div>
        <span>Ships from {content.from}</span>
        <span>
          Delivery: Dispatched in <b>{content.dispatchTime}</b>
        </span>
        <span>
          {content.to ? `Shipping to ${content.to}: ` : 'Shipping price: '}
          <b>{content.price}</b>
        </span>
      </div>

      <div>
        <span>
          Taxes may occur in checkout.{' '}
          <a
            href={TAXES_INFO_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="More details about taxes"
          >
            More details
          </a>
        </span>
      </div>
    </StyledDisplayShippingContainer>
  );
};

export default DisplayShipping;
